import React from 'react';
import TextField from '@mui/material/TextField';
import { phrases, validator } from 'myValidation';
import { Controller, useWatch } from 'react-hook-form';
import { FormControl } from '@mui/material';
import InputBase from '@mui/material/InputBase';
import { withStyles } from '@mui/styles';
import CurrencyTextField from 'components/CurrencyTextFieldCompat';
import clsx from 'clsx';
import MaskedInput from 'react-text-mask';
import persianJs from 'persianjs';
import { FieldWrapper } from 'components';
import { useStyles } from './Style';

const BootstrapInput = withStyles((theme) => ({
  root: {},

  input: {
    borderRadius: (props) => {
      const rightRadius = props.removeRightRadius;
      const leftRadius = props.removeLeftRadius;
      return `${leftRadius ? 0 : 8}px ${rightRadius ? 0 : 8}px ${rightRadius ? 0 : 8}px ${
        leftRadius ? 0 : 8
      }px`;
    },
    position: 'relative',
    border: '1px solid #dfdfdf',
    padding: '12px',
    transition: "all .1s ease-in",
    fontFamily: (props) => (props.farsiDigitFont ? 'Vazir FD' : 'Vazir FD'),
    '&::placeholder': {
      direction: 'ltr',
      textAlign: 'left',
    },
  },
}))(InputBase);

const CharacterCounter = ({ control, name, limit, defaultValue, className, errorClassName }) => {
  const value = useWatch({ control, name, defaultValue });
  const characterCount = String(value ?? '').length;

  return (
    <span className={clsx(className, { [errorClassName]: characterCount > limit })}>
      {characterCount.toLocaleString('fa-IR')} / {limit.toLocaleString('fa-IR')}
    </span>
  );
};

const InputV2 = (props) => {
  let {
    name,
    defaultValue = '',
    register,
    errors,
    rules,
    label,
    labelStyle,
    control,
    setValue,
    forceValue,
    onChangeValue,
    currency = false,
    currencyText,
    masked,
    mainStyle = {},
    englishNumber,
    className,
    hasMargin = true,
    removeLeftRadius = false,
    removeRightRadius = false,
    useDefaultMultiline = false,
    typeRules = {},
    tooltip,
    characterLimit,
    ...rest
  } = props;
  // end props
  const vv = {};
  const classes = useStyles({ removeLeftRadius, removeRightRadius });
  const fieldWrapperInfoText =
    characterLimit && control ? (
      <CharacterCounter
        control={control}
        name={name}
        limit={characterLimit}
        defaultValue={defaultValue}
        className={classes.characterCounter}
        errorClassName={classes.characterCounterError}
      />
    ) : (
      props.infoText
    );
  const fixedRowsInputProps =
    rest.multiline && rest.rows && !useDefaultMultiline
      ? {
          inputComponent: 'textarea',
          inputProps: { ...rest.inputProps, rows: rest.rows },
        }
      : {};

  if (!label) {
    let nameField = name;

    if (name.includes('[')) {
      nameField = name.split('[')[0];
    }

    if (name.includes('.')) {
      nameField = `${nameField}.${name.split('.').pop()}`;
    }

    label = phrases.attributes[nameField.replace(/\s*\[.*?\]\s*/g, '')];
  }

  if (rules) {
    rules.forEach((item) => {
      // with options
      if (item.includes('|')) {
        let [funcName, options] = item.split('|');
        const func = validator.validators[funcName];

        if (options.includes('{')) {
          options = JSON.parse(options);
        }

        vv[item] = (value) => {
          if (!func(value, options)) {
            return phrases[funcName].replace(':attribute', label).replace(':param', options);
          }
          return true;
        };
      } else {
        const func = validator.validators[item];
        vv[item] = (value) =>
          !func(String(value)) ? phrases[item].replace(':attribute', label) : true;
      }
    });
  }

  let type = 'normal';

  if (currency) {
    type = 'currency';
  } else if (masked) {
    type = 'masked';
  }
  const numbersToEn = (e) => {
    const copyE = { ...e };
    if (e?.target?.value) {
      copyE.target.value = persianJs(e?.target?.value).toEnglishNumber().toString();
    }
    return copyE;
  };

  // render
  if (register) {
    return (
      <FieldWrapper {...props} infoText={fieldWrapperInfoText}>
        <Controller
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl
              className={clsx(classes.root, {
                [classes.margin]: hasMargin,
              })}
              style={{ width: '100%', ...mainStyle }}
            >
              {type === 'currency' ? (
                <CurrencyTextField
                  modifyValueOnWheel={false}
                  label={null}
                  {...rest}
                  onChange={(event, value) => {
                    // TODO: it shoud pass event like other inputs
                    const newV = !value
                      ? value
                      : englishNumber
                        ? persianJs(value ?? '')
                          .toEnglishNumber()
                          .toString()
                        : value;
                    onChange(newV);
                    if (onChangeValue) onChangeValue(newV);
                  }}
                  value={forceValue ?? value}
                  id={`bootstrap-input${name}`}
                  className={clsx(classes.currencyInput)}
                  allowDecimalPadding={false}
                  variant="outlined"
                  currencySymbol={<div className={clsx(classes.currencyText)}>{currencyText}</div>}
                  outputFormat="string"
                  autoComplete="off"
                />
              ) : null}

              {type === 'masked' ? (
                <MaskedInput
                  label={null}
                  {...rest}
                  className={clsx(classes.maskInput)}
                  variant="outlined"
                  guide={false}
                  id={`bootstrap-input-${name}`}
                  value={forceValue ?? value}
                  onChange={(e) => {
                    const newE = englishNumber ? numbersToEn(e) : e;
                    onChange(newE);
                    if (onChangeValue) onChangeValue(newE);
                  }}
                  autocomplete="off"
                />
              ) : null}

              {type === 'normal' && (
                <BootstrapInput
                  {...rest}
                  {...fixedRowsInputProps}
                  className={clsx(classes.bootstrapInput)}
                  onChange={(e) => {
                    const newE = englishNumber ? numbersToEn(e) : e;
                    onChange(newE);
                    if (onChangeValue) onChangeValue(newE);
                  }}
                  value={forceValue ?? value}
                  id={`bootstrap-input-${name}`}
                  autoComplete="off"
                  removeLeftRadius={removeLeftRadius}
                  removeRightRadius={removeRightRadius}
                />
              )}
            </FormControl>
          )}
          name={name}
          defaultValue={defaultValue}
          rules={{
            validate: {
              ...vv,
            },
            ...typeRules,
          }}
        />
      </FieldWrapper>
    );
  }
  if (forceValue != null || onChangeValue != null) {
    return (
      <FieldWrapper {...props} infoText={fieldWrapperInfoText}>
        <FormControl
          className={clsx({
            [classes.margin]: hasMargin,
          })}
          style={{ width: '100%', ...mainStyle }}
        >
          {type === 'currency' ? (
            <CurrencyTextField
              modifyValueOnWheel={false}
              label={null}
              {...rest}
              onChange={(event, value) => {
                const newV = !value
                  ? value
                  : englishNumber
                    ? persianJs(value ?? '')
                      .toEnglishNumber()
                      .toString()
                    : value;
                if (onChangeValue) onChangeValue(newV);
              }}
              value={forceValue}
              id={`bootstrap-input-${name}`}
              className={classes.currencyInput}
              allowDecimalPadding={false}
              variant="outlined"
              currencySymbol={<div className={clsx(classes.currencyText)}>{currencyText }</div>}
              outputFormat="string"
              dir="ltr"
              autoComplete="off"
            />
          ) : null}

          {type === 'masked' ? (
            <MaskedInput
              label={null}
              {...rest}
              className={classes.maskInput}
              variant="outlined"
              guide={false}
              id={`bootstrap-input-${name}`}
              value={forceValue}
              onChange={(e) => {
                const newE = englishNumber ? numbersToEn(e) : e;
                if (onChangeValue) onChangeValue(newE);
              }}
              autoComplete="off"
            />
          ) : null}

          {type === 'normal' ? (
            <BootstrapInput
              {...rest}
              {...fixedRowsInputProps}
              className={clsx(classes.bootstrapInput)}
              onChange={(e) => {
                const newE = englishNumber ? numbersToEn(e) : e;
                if (onChangeValue) onChangeValue(newE);
              }}
              value={forceValue}
              id={`bootstrap-input${name}`}
              removeLeftRadius={removeLeftRadius}
              removeRightRadius={removeRightRadius}
              autoComplete="off"
            />
          ) : null}
        </FormControl>
      </FieldWrapper>
    );
  }
  return (
    <FieldWrapper {...props} infoText={fieldWrapperInfoText}>
      <TextField
        {...rest}
        defaultValue={defaultValue}
        error={errors ? !!errors?.[name] : false}
        name={name}
        variant="outlined"
        autoComplete="off"
      />
    </FieldWrapper>
  );
};

export default InputV2;
